import { Eyebrow } from '~/components/ui/Eyebrow'
import { parseYoutubeEmbed } from '~/lib/media/youtube'
import { YouTubeEmbed } from '~/components/ui/YouTubeEmbed'
import { useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { getCampaign, orNotFound } from '~/lib/content/content'
import { cacheHeaders } from '~/lib/site/cache'
import { excerpt, pageMeta } from '~/lib/site/seo'
import { RichText } from '~/components/ui/RichText'
import { DonateSignupBand } from '~/components/sections/DonateSignupBand'
import { PageTitle } from '~/components/ui/PageTitle'
import { pageTop } from '~/components/ui/PageHero'

// Text column: 940px on desktop, 728px below lg, and the page gutter on mobile.
// (Not `Container`, whose width is a percentage of the viewport.)
const column = 'mx-auto max-w-[940px] px-5 md:px-0 max-lg:max-w-[728px]'

// <dxe-petition> (a web component from the petitions service) is typed in
// src/types/jsx.d.ts.
const PETITION_SCRIPT = 'https://storage.googleapis.com/dxe-petitions/api.js'

export const Route = createFileRoute('/campaigns/$slug')({
  loader: ({ params }) => orNotFound(getCampaign({ data: params.slug })),
  headers: () => cacheHeaders.content,
  head: ({ loaderData: c }) => {
    if (!c) return {}
    const description =
      c.summary ||
      excerpt(c.html) ||
      `${c.title}: a Direct Action Everywhere campaign for animals.`
    return pageMeta({
      title: c.title,
      description,
      ogTitle: c.title,
      image: c.image?.src,
    })
  },
  component: Campaign,
})

// api.js is not a real custom element: it does a single
// document.querySelectorAll('dxe-petition') scan when the script executes and
// fills each element's innerHTML. It exposes no init function and has no
// observer, so after a client-side navigation (script already loaded, new
// <dxe-petition> mounted) nothing would populate the element. To re-scan we
// replace the script tag, which makes the browser re-execute it (cached).
// This runs in an effect, i.e. after hydration, so the server-rendered empty
// element is never hydrated against injected children.
function usePetitionScript(slug: string | undefined) {
  useEffect(() => {
    if (!slug) return
    const el = document.querySelector('dxe-petition')
    if (!el || el.childElementCount > 0) return
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${PETITION_SCRIPT}"]`,
    )
    // Still loading: it will scan (and find our element) when it executes.
    if (existing && existing.dataset.loaded !== 'true') return
    existing?.remove()
    const s = document.createElement('script')
    s.src = PETITION_SCRIPT
    s.async = true
    s.onload = () => {
      s.dataset.loaded = 'true'
    }
    document.head.appendChild(s)
  }, [slug])
}

function Campaign() {
  const c = Route.useLoaderData()
  const video = parseYoutubeEmbed(c.video)
  usePetitionScript(c.petition?.slug)
  return (
    <>
      <div className={`${pageTop} pb-10 md:pb-5`}>
        <div className={column}>
          <Eyebrow variant="kicker">Campaign</Eyebrow>
          <PageTitle className="mb-5">{c.title}</PageTitle>
        </div>
        {(c.video || c.videoHtml) && (
          <div className={`${column} mt-5`}>
            {c.videoHtml && (
              <RichText
                html={c.videoHtml}
                className="prose-headings:text-fg! prose-headings:mb-0!"
              />
            )}
            {video ? (
              <YouTubeEmbed
                id={video.id}
                query={video.query}
                title={`${c.title} video`}
              />
            ) : (
              c.video && (
                <div className="relative pt-[56.25%]">
                  <iframe
                    src={c.video}
                    title={`${c.title} video`}
                    className="absolute inset-0 h-full w-full"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                  />
                </div>
              )
            )}
          </div>
        )}
      </div>

      {c.petition && (
        <section
          id="Push"
          className="bg-surface-alt py-10 max-md:pt-0 max-md:pb-6"
        >
          <div className={`${column} my-10 max-md:mb-5 max-md:pt-5`}>
            <RichText
              html={c.petition.html}
              className="text-center text-fg-body prose-headings:text-fg! prose-headings:mb-3! [&_h1]:text-[32px] [&_h1]:leading-10"
            />
            <div className="mt-10">
              <dxe-petition
                key={c.petition.slug}
                petition={c.petition.slug}
                cta={c.petition.cta ?? ''}
              />
              <p className="mt-4 text-center text-sm">
                By signing, you agree to receive email messages from Direct
                Action Everywhere.
                <br />
                You may unsubscribe at any time.
              </p>
            </div>
          </div>
        </section>
      )}

      <div className="pb-5">
        {c.html && (
          <div className={column}>
            <RichText html={c.html} variant="campaign" className="py-5" />
          </div>
        )}
      </div>
      <DonateSignupBand />
    </>
  )
}
